import { useState, useEffect } from "react";
import axios from "axios";
import { DateField, useNotify, type DataTableColumn } from "@routes-plan/ui";
import { Office } from "../../models/office";
import { Route } from "../../models/route";
import { minYmd, nowOperationalDateString } from "../../utils/operationalDateTime";
import { useT } from "../../i18n";
import { formatCurrency } from "../../utils/format/currency";
import {
  OfficeSelect,
  ReportEmpty,
  ReportFilters,
  ReportPage,
  ReportResults,
  ReportTable,
  RouteMultiSelect,
} from "./components/ReportLayout";

/** Fila que entrega el API (POST /reports/loans/late). */
interface LateLoanApiRow {
  id: number;
  idNumber: string;
  fullName: string;
  loanValue: number;
  balance: number;
  periodPayments: number;
  lastPaymentDate: string | null;
  daysLate: number;
  route: { id: number; name: string };
}

interface LateLoanRow {
  id: number | string;
  idNumber: string;
  fullName: string;
  loanValue: number;
  value: number;
  paymentDate: string;
  balance: number;
  daysLate: number;
  route: string;
}

/** Códigos de error del API → texto de pantalla. */
const ERROR_KEYS = {
  INVALID_DATES: "loanReports.lateLoans.errorDates",
  START_AFTER_END: "loanReports.lateLoans.errorStartAfterEnd",
  FUTURE_DATE: "loanReports.lateLoans.errorFuture",
  RANGE_TOO_LONG: "loanReports.lateLoans.errorRange",
  ROUTE_REQUIRED: "loanReports.lateLoans.errorRoutes",
  FORBIDDEN: "loanReports.lateLoans.errorForbidden",
} as const;

/** Préstamos activos con días de atraso hoy, con lo abonado en el periodo. */
const LateLoans = () => {
  const t = useT();
  const notify = useNotify();
  const [office_id, setOffice_id] = useState("");
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [start_date, setStart_date] = useState("");
  const [end_date, setEnd_date] = useState("");
  const [fechas, setFechas] = useState<LateLoanApiRow[]>([]);
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [queried, setQueried] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    // Validación antes de consultar (el API vuelve a validar).
    const local = !start_date || !end_date
      ? "loanReports.lateLoans.errorDates"
      : start_date > end_date
        ? "loanReports.lateLoans.errorStartAfterEnd"
        : selectedRouteIds.length === 0
          ? "loanReports.lateLoans.errorRoutes"
          : null;
    if (local) {
      notify.warning(t(local as (typeof ERROR_KEYS)[keyof typeof ERROR_KEYS]));
      return;
    }
    const formData = {
      endDate: end_date,
      startDate: start_date,
      routeId: selectedRouteIds,
    };
    const url = "/reports/loans/late";
    setQueried(true);
    setLoading(true);
    try {
      const response = await axios.post(url, formData);
      setFechas(response.data.data ?? []);
    } catch (error: any) {
      setFechas([]);
      const code = error?.response?.data?.code as keyof typeof ERROR_KEYS | undefined;
      const status = error?.response?.status;
      notify.error(
        code && ERROR_KEYS[code]
          ? t(ERROR_KEYS[code])
          : status === 403
            ? t("loanReports.lateLoans.errorForbidden")
            : t("loanReports.lateLoans.loadError"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (office_id) {
      (async () => {
        try {
          const { data } = await axios.get(`offices/${office_id}/routes`);
          setOfficeRoutes(Array.isArray(data) ? data : []);
          setSelectedRouteIds([]);
        } catch (error) {
          notify.error(t("loanReports.routesLoadError"));
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [office_id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("offices");
        setOffices(data?.data ?? []);
      } catch (error) {
        notify.error(t("loanReports.officesLoadError"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows: LateLoanRow[] = fechas.map((r) => ({
    id: r.id,
    idNumber: r.idNumber ?? "",
    fullName: r.fullName ?? "",
    loanValue: Number(r.loanValue ?? 0),
    value: Number(r.periodPayments ?? 0),
    paymentDate: r.lastPaymentDate ?? "",
    balance: Number(r.balance ?? 0),
    daysLate: Number(r.daysLate ?? 0),
    route: r.route?.name ?? "",
  }));

  const columns: DataTableColumn<LateLoanRow>[] = [
    { id: "id", header: t("loanReports.loanNumber"), value: (r) => r.id, sortable: true },
    { id: "idNumber", header: t("common.idNumber"), value: (r) => r.idNumber, sortable: true, hideBelow: "sm" },
    { id: "fullName", header: t("loanReports.client"), value: (r) => r.fullName, sortable: true },
    { id: "loanValue", header: t("loanReports.loanValue"), value: (r) => r.loanValue, render: (r) => formatCurrency(r.loanValue), align: "right", sortable: true },
    { id: "value", header: t("loanReports.lateLoans.periodPayments"), value: (r) => r.value, render: (r) => formatCurrency(r.value), align: "right", sortable: true },
    { id: "paymentDate", header: t("loanReports.lateLoans.lastPayment"), value: (r) => r.paymentDate, render: (r) => r.paymentDate || "—", sortable: true },
    { id: "balance", header: t("loanReports.balance"), value: (r) => r.balance, render: (r) => formatCurrency(r.balance), align: "right", sortable: true },
    { id: "daysLate", header: t("loanReports.daysLate"), value: (r) => r.daysLate, align: "right", sortable: true },
    { id: "route", header: t("summaryReports.shared.route"), value: (r) => r.route, sortable: true },
  ];

  return (
    <ReportPage title={t("loanReports.lateLoans.title")} description={t("loanReports.lateLoans.description")}>
      <ReportFilters onSubmit={() => void submit()} submitDisabled={loading} submitButtonProps={{ "data-tour": "late-loans-query" }}>
        <DateField
          label={t("loanReports.startDate")}
          data-tour="late-loans-start"
          value={start_date || null}
          onChange={(v) => setStart_date(v ?? "")}
          max={minYmd(end_date || nowOperationalDateString(), nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t("loanReports.endDate")}
          data-tour="late-loans-end"
          value={end_date || null}
          onChange={(v) => setEnd_date(v ?? "")}
          min={start_date || undefined}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={office_id} onChange={setOffice_id} required tourId="late-loans-office" />
        <RouteMultiSelect routes={officeRoutes} value={selectedRouteIds} onChange={setSelectedRouteIds} tourId="late-loans-routes" />
      </ReportFilters>

      <ReportResults>
        <ReportTable<LateLoanRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          exportFileName={t("loanReports.lateLoans.exportFile")}
          tourId="late-loans"
          ariaLabel={t("loanReports.lateLoans.title")}
          initialSort={{ columnId: "daysLate", direction: "desc" }}
          emptyState={<ReportEmpty queried={queried} />}
        />
      </ReportResults>
    </ReportPage>
  );
};

export default LateLoans;
